import React, { useEffect, useState } from 'react';
import { MenuItem, TextField } from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import enLocale from 'date-fns/locale/en-US';
import esLocale from 'date-fns/locale/es';
import { useStoreState } from 'easy-peasy';
import { useParams } from 'react-router-dom';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import { FormattedMessage, useIntl } from 'react-intl';
import useMedicalRecordFetch from 'api/MedicalRecordsFetch';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { paymentMethodToPriceCategoryMap } from 'constants/paymentMethods';
import { allFields } from 'components/Modals/RestoreFieldMedicalRecord';
import Axios from 'axios';
import MaterialDialog from './Dialog';
import MaterialSelect from './Select';
import Virtualize from './VirtualizedAutocomplete';

const localeMap = {
  es: esLocale,
  en: enLocale,
};

const paymentTypeOptions = [
  { value: 'cash', name: <FormattedMessage id="assign_inventory.cash" /> },
  {
    value: 'insurance',
    name: <FormattedMessage id="assign_inventory.insurance" />,
  },
  {
    value: 'card',
    name: <FormattedMessage id="assign_inventory.credit_card" />,
  },
];

export const AssignCatalogItemModal = ({ open, toggle, selectedSubcategory }) => {
  const lang = useStoreState((state) => state.lang);
  // const catalog = useStoreState((state) => state.catalogues);
  const [catalog, setCatalog] = useState([]);
  const pMap = useStoreState((state) => state.patientsMap);
  const intl = useIntl();
  const { patient: patientId, recordId } = useParams();
  const patient = pMap[patientId];
  const { addCatalogItem, getCatalogLight } = useMedicalRecordFetch();
  const [loading, setLoading] = useState(false);

  const [catalogLoading, setCatalogLoading] = useState(false);

  const loadCatalog = async (params) => {
    try {
      setCatalogLoading(true);
      const catalogRes = await getCatalogLight(params);
      setCatalogLoading(false);
      setCatalog(catalogRes.data);
      console.log(catalog);
    } catch (err) {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setCatalog([]);
      loadCatalog({ limit: 10000, skip: 0, categoria: paymentMethodToPriceCategoryMap[patient.paymentType], subcategory: selectedSubcategory });
    }
  }, [open]);

  const formSubmit = async (values, formContext) => {
    try {
      setLoading(true);
      await addCatalogItem({ ...values, patientId, medicalRecordId: recordId });
      formContext.resetForm();
      toggle();
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const { values, setFieldValue, handleChange, handleSubmit, errors, touched, resetForm } = useFormik({
    initialValues: {
      expiry_date: new Date(),
      catalogItemId: '',
      paymentType: '',
      comment: '',
      amount: '',
      fieldToSaveLink: '',
    },
    validationSchema: Yup.object({
      catalogItemId: Yup.string().required(),
      paymentType: Yup.string().required(),
      expiry_date: Yup.date().required(),
      comment: Yup.string(),
      amount: Yup.number().integer().positive().required(),
      fieldToSaveLink: selectedSubcategory === 'exam' ? Yup.string().required() : Yup.string(),
    }),
    onSubmit: formSubmit,
  });

  useEffect(() => {
    resetForm();
  }, [open]);

  return (
    <MaterialDialog
      opened={open}
      title={<FormattedMessage id="inventory.add_new_item" />}
      onClose={toggle}
      actions={[
        {
          name: <FormattedMessage id="calendar.add" />,
          onClick: handleSubmit,
          color: 'primary',
          disabled: loading,
        },
        {
          name: <FormattedMessage id="calendar.cancel" />,
          onClick: toggle,
          color: 'secondary',
          disabled: loading,
        },
      ]}
    >
      <Virtualize
        options={catalog.map(({ _id, descripcion, categoria }) => ({
          value: _id,
          name: `${descripcion} (${intl.formatMessage({
            id: 'catalogue.price_category',
          })}: ${categoria || 0})`,
        }))}
        onChange={(_, value) => {
          setFieldValue('catalogItemId', value.value);
        }}
        error={touched.catalogItemId && errors.catalogItemId}
        value={values.catalogItemId}
        label={<FormattedMessage id="catalogue.name" />}
      />
      <MaterialSelect
        title={<FormattedMessage id="user.payment_type" />}
        onChange={handleChange}
        options={paymentTypeOptions}
        name="paymentType"
        value={values.paymentType}
        error={touched.paymentType && errors.paymentType}
        style={{ marginBottom: '12px' }}
      />
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap[lang]}>
        <DatePicker
          fullWidth
          format="dd.MM.yyyy"
          style={{ marginBottom: '12px' }}
          id="time-picker"
          inputVariant="outlined"
          label={<FormattedMessage id="assign_request.deadline" />}
          onChange={(date) => {
            setFieldValue('expiry_date', date);
          }}
          value={values.expiry_date}
          error={touched.expiry_date && errors.expiry_date}
          KeyboardButtonProps={{
            'aria-label': 'change time',
          }}
        />
      </MuiPickersUtilsProvider>
      <TextField
        onChange={handleChange}
        value={values.amount}
        label={<FormattedMessage id="shared.quantity" />}
        name="amount"
        type="number"
        error={touched.amount && errors.amount}
        helperText={touched.amount && errors.amount}
        variant="outlined"
        fullWidth
        style={{ marginBottom: '12px' }}
      />
      <TextField
        onChange={handleChange}
        value={values.comment}
        label={<FormattedMessage id="inventory.doctor_comment" />}
        name="comment"
        error={touched.comment && errors.comment}
        helperText={touched.comment && errors.comment}
        variant="outlined"
        fullWidth
        style={{ marginBottom: '12px' }}
      />
      {selectedSubcategory === 'exam' && (
        <TextField
          select
          variant="outlined"
          fullWidth
          style={{ marginBottom: '12px' }}
          name="fieldToSaveLink"
          label={intl.formatMessage({ id: 'medical_record.field' })}
          value={values.fieldToSaveLink}
          error={Boolean(touched.fieldToSaveLink && errors.fieldToSaveLink)}
          onChange={handleChange}
        >
          {allFields.map((field) => (
            <MenuItem key={field.name} value={field.name}>
              {intl.formatMessage({ id: field.translationKey })}
            </MenuItem>
          ))}
        </TextField>
      )}
    </MaterialDialog>
  );
};
